"use client";

import { useMemo, useState } from "react";

import { GlassCard } from "@/components/ui/GlassCard";
import { SectionHeading } from "@/components/ui/SectionHeading";

// The share of writing time saved is the visitor's own estimate (no measured figure yet), starting at
// a middling 50%. Replace the default once the app's time-to-completion data supports a number.
const DEFAULT_PERCENT_SAVED = 50;
const WEEKS_PER_YEAR = 48; // accounts for vacation / closures

// `withHeading={false}` on /roi, whose own hero already introduces the calculator.
export function RoiCalculator({ withHeading = true }: { withHeading?: boolean }) {
  const [specialists, setSpecialists] = useState(2);
  const [reportsPerWeek, setReportsPerWeek] = useState(40);
  const [minutesPerReport, setMinutesPerReport] = useState(12);
  const [hourlyValue, setHourlyValue] = useState(180);
  const [percentSaved, setPercentSaved] = useState(DEFAULT_PERCENT_SAVED);

  const results = useMemo(() => {
    const minutesSavedPerWeek = reportsPerWeek * minutesPerReport * (percentSaved / 100);
    const hoursSavedPerWeek = minutesSavedPerWeek / 60;
    const yearlyHours = hoursSavedPerWeek * WEEKS_PER_YEAR;
    const yearlyDollars = yearlyHours * hourlyValue;
    const specialistMonthlyCost = specialists * 279;
    const yearlyCost = specialistMonthlyCost * 12;
    const netSavings = yearlyDollars - yearlyCost;
    return {
      hoursPerWeek: Math.round(hoursSavedPerWeek * 10) / 10,
      yearlyHours: Math.round(yearlyHours),
      yearlyDollars: Math.round(yearlyDollars),
      yearlyCost,
      netSavings: Math.round(netSavings),
    };
  }, [specialists, reportsPerWeek, minutesPerReport, hourlyValue, percentSaved]);

  return (
    <section id="roi" className={withHeading ? "relative py-20 sm:py-28" : "relative pb-20 pt-4 sm:pb-28"}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {withHeading && (
          <SectionHeading
            eyebrow="ROI"
            title="See what your practice gets back"
            description="A back-of-napkin estimate using your numbers. Adjust the inputs to match your reality."
          />
        )}
        <div className={`${withHeading ? "mt-12" : ""} grid gap-8 lg:grid-cols-[1.05fr_0.95fr]`}>
          <GlassCard tone="tinted">
            <div className="space-y-5">
              <NumberField label="Specialists in your practice" value={specialists} onChange={setSpecialists} min={1} max={50} />
              <NumberField label="Reports written per week (whole practice)" value={reportsPerWeek} onChange={setReportsPerWeek} min={1} max={500} />
              <NumberField label="Minutes spent per report today" value={minutesPerReport} onChange={setMinutesPerReport} min={1} max={60} />
              <NumberField label="Specialist hourly value (CAD)" value={hourlyValue} onChange={setHourlyValue} min={50} max={1000} step={10} />
              <NumberField label="Writing time Refdocs saves (%)" value={percentSaved} onChange={setPercentSaved} min={10} max={90} step={5} />
              <p className="text-xs text-muted">
                An estimate, not a measurement: set the share of writing time you expect to save. The Specialist plan is $279 CAD per specialist per month.
              </p>
            </div>
          </GlassCard>
          <GlassCard className="flex flex-col gap-4">
            <ResultRow label="Time saved per week" value={`${results.hoursPerWeek} hrs`} />
            <ResultRow label="Time saved per year" value={`${results.yearlyHours.toLocaleString()} hrs`} />
            <ResultRow label="Value of time recovered" value={`$${results.yearlyDollars.toLocaleString()} / year`} emphasize />
            <ResultRow label="Refdocs cost (Specialist plan)" value={`$${results.yearlyCost.toLocaleString()} / year`} muted />
            {results.netSavings >= 0 ? (
              <div className="mt-4 rounded-xl bg-gradient-to-br from-[#5a48d4] to-[#3a2cad] p-5 text-white shadow-lg shadow-[#3a2cad]/30">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/80">Estimated net savings</p>
                <p className="mt-1 text-3xl font-semibold">
                  ${results.netSavings.toLocaleString()}
                  <span className="ml-1 text-sm font-normal text-white/80">/ year</span>
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-ink/10 bg-white/70 p-5 text-ink">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">Estimated net cost</p>
                <p className="mt-1 text-3xl font-semibold">
                  −${Math.abs(results.netSavings).toLocaleString()}
                  <span className="ml-1 text-sm font-normal text-muted">/ year</span>
                </p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </section>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-body">{label}</span>
        <span className="text-sm font-semibold text-accentStrong">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#5a48d4]"
      />
    </label>
  );
}

function ResultRow({ label, value, emphasize, muted }: { label: string; value: string; emphasize?: boolean; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-white/50 pb-3 last:border-0">
      <p className="text-sm text-muted">{label}</p>
      <p className={`whitespace-nowrap text-right text-lg font-semibold ${emphasize ? "text-accentStrong" : muted ? "text-muted" : "text-ink"}`}>
        {value}
      </p>
    </div>
  );
}
